import React from 'react'
import toast, { Toaster } from 'react-hot-toast'
import i18n from '../../i18n'
import '../../styles/Toast.css'

export const TOAST_TYPES = ['success', 'error', 'warning', 'info']

// Failures stay on screen longer so they can actually be read.
export const TOAST_DURATIONS = {
    success: 3000,
    info: 4000,
    warning: 4000,
    error: 5000,
}

const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const AlertIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M12 6.5v7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
        <circle cx="12" cy="17.5" r="1.6" fill="currentColor" />
    </svg>
)

const InfoIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="6.8" r="1.6" fill="currentColor" />
        <path d="M12 10.5v7" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" />
    </svg>
)

const CloseIcon = () => (
    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
        <path d="M1.5 1.5l9 9M10.5 1.5l-9 9" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
)

const ICONS = {
    success: CheckIcon,
    error: AlertIcon,
    warning: AlertIcon,
    info: InfoIcon,
}

/**
 * Presentational toast rendered through react-hot-toast's toast.custom().
 * Visibility, stacking, pause-on-hover and removal are handled by the library.
 */
const AppToast = ({ t, message, type }) => {
    const Icon = ICONS[type]

    return (
        <div className={`app-toast app-toast--${type} ${t.visible ? 'app-toast--visible' : 'app-toast--hidden'}`} {...t.ariaProps}>
            <span className="app-toast-icon">
                <Icon />
            </span>
            <div className="app-toast-message">{message}</div>
            <button type="button" className="app-toast-close" onClick={() => toast.dismiss(t.id)} aria-label={i18n.t('close')}>
                <CloseIcon />
            </button>
            {Number.isFinite(t.duration) && <span className="app-toast-progress" style={{ animationDuration: `${t.duration}ms` }} />}
        </div>
    )
}

/**
 * Shows an app-styled toast.
 * @param {React.ReactNode} message
 * @param {'success'|'error'|'warning'|'info'} [type='success']
 * @param {object} [options] extra react-hot-toast options (e.g. duration, id)
 */
export const showToast = (message, type = 'success', options = {}) => {
    const variant = TOAST_TYPES.includes(type) ? type : 'info'
    const isError = variant === 'error'

    return toast.custom((t) => <AppToast t={t} message={message} type={variant} />, {
        duration: TOAST_DURATIONS[variant],
        ariaProps: {
            role: isError ? 'alert' : 'status',
            'aria-live': isError ? 'assertive' : 'polite',
        },
        ...options,
    })
}

/** Shared <Toaster/> so every mount point (App and modals) is configured identically. zIndex keeps toasts above modals. */
export const AppToaster = () => (
    <Toaster position="top-right" reverseOrder={false} gutter={12} containerClassName="app-toaster" containerStyle={{ top: 24, right: 24, left: 16, bottom: 16, zIndex: 99999 }} />
)

export default AppToast
